import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import { toast } from '@/lib/notifications';
import type { BillingSettings, UpdateBillingSettingsInput } from '@etiquetachef/types';
import { updateBillingSettingsCallable } from '@/lib/callables';
import { trackEvents } from '@/lib/events';

type Props = {
  settings: BillingSettings;
};

type FormState = {
  billing_email_enabled: boolean;
  invoice_created_email_enabled: boolean;
  invoice_due_soon_email_enabled: boolean;
  invoice_overdue_email_enabled: boolean;
  payment_success_email_enabled: boolean;
  payment_failed_email_enabled: boolean;
  payment_action_required_email_enabled: boolean;
  due_soon_days_before: string;
  overdue_reminder_days: string;
  suspend_after_days_overdue: string;
  next_invoice_days_before_due: string;
};

/** `[7, 3, 1]` → `"7, 3, 1"` para o campo de texto. */
function arrayToCsv(arr: number[]): string {
  return arr.join(', ');
}

/** Campo CSV → inteiros positivos únicos, ordenados; `null` se algum token não for. */
function csvToSortedArray(csv: string): number[] | null {
  const parts = csv
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const nums: number[] = [];
  for (const p of parts) {
    const n = Number(p);
    if (!Number.isInteger(n) || n <= 0) return null;
    nums.push(n);
  }
  return [...new Set(nums)].sort((a, b) => a - b);
}

/** Doc de settings → estado do formulário (listas viram CSV, números viram texto). */
function fromSettings(s: BillingSettings): FormState {
  return {
    billing_email_enabled: s.billing_email_enabled,
    invoice_created_email_enabled: s.invoice_created_email_enabled,
    invoice_due_soon_email_enabled: s.invoice_due_soon_email_enabled,
    invoice_overdue_email_enabled: s.invoice_overdue_email_enabled,
    payment_success_email_enabled: s.payment_success_email_enabled,
    payment_failed_email_enabled: s.payment_failed_email_enabled,
    payment_action_required_email_enabled: s.payment_action_required_email_enabled,
    due_soon_days_before: arrayToCsv(s.due_soon_days_before),
    overdue_reminder_days: arrayToCsv(s.overdue_reminder_days),
    suspend_after_days_overdue: String(s.suspend_after_days_overdue),
    next_invoice_days_before_due: String(s.next_invoice_days_before_due),
  };
}

const TOGGLE_KEYS = [
  'billing_email_enabled',
  'invoice_created_email_enabled',
  'invoice_due_soon_email_enabled',
  'invoice_overdue_email_enabled',
  'payment_success_email_enabled',
  'payment_failed_email_enabled',
  'payment_action_required_email_enabled',
] as const;

/**
 * Formulário das configurações globais de cobrança (`/billing/emails`, aba
 * Configurações): interruptores dos e-mails e janelas em dias.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16).
 *
 * Contrato: recebe `settings` já lidos pelo pai e reinicia o formulário quando
 * eles mudam. Validação local: listas CSV não vazias de inteiros > 0;
 * `suspend_after_days_overdue` > 0; `next_invoice_days_before_due` ≥ 0. Envia
 * o doc inteiro por `updateBillingSettingsCallable` (o servidor aceita patch
 * parcial, mas a tela manda tudo) e registra `billingSettingsUpdated`.
 *
 * O servidor exige `ADMIN_PERMISSIONS.updateBillingSettings` (ROOT/FINANCE).
 *
 * Usado por: `routes/BillingEmailsPage.tsx` (rota `/billing/emails`, menu
 * Cobrança → E-mails; spec `docs/screens/billing-emails.md`), admin web.
 *
 * Arquitetura: estas janelas são lidas pelo scheduler `dailyBillingTick` e
 * pelo webhook (`suspend_after_days_overdue`); mudar aqui muda o comportamento
 * de cobrança de TODOS os tenants — não há valor por tenant.
 *
 * Dados: `/config/billing/settings/singleton` (`BillingSettingsSchema` e
 * `UpdateBillingSettingsInput`, `packages/types/src/schemas.ts`), leitura
 * `isSuperAdmin()` nas rules, escrita só pelo callable
 * (`apps/functions/src/admin/updateBillingSettings.ts`). Dias são inteiros,
 * não ms.
 *
 * Histórico:
 *  - 2026-06-08 22:20 +0200 — Cloudscape (39-E).
 *  - 2026-06-10 10:44 +0200 — `payment_action_required_email_enabled`
 *    (suspensão no primeiro `past_due`).
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu, permissão; doc do envio (#796).
 */
export function BillingSettingsForm({ settings }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => fromSettings(settings));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(fromSettings(settings));
  }, [settings]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  function setBool(key: (typeof TOGGLE_KEYS)[number], value: boolean): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  /**
   * Valida o formulário e grava o doc inteiro via `updateBillingSettings`.
   *
   * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16).
   *
   * Contrato: listas CSV vazias/inválidas, `suspend_after_days_overdue` ≤ 0 ou
   * `next_invoice_days_before_due` < 0 → erro local, sem rede. Sucesso →
   * `trackEvents.billingSettingsUpdated(chaves)` e toast; erro → `err.message`.
   * Efeito global: todos os tenants (ver docstring do componente).
   *
   * Dados — input (dias inteiros, listas ordenadas e sem repetição):
   *   { "billing_email_enabled": true, …, "due_soon_days_before": [1, 3, 7],
   *     "overdue_reminder_days": [1, 3, 7], "suspend_after_days_overdue": 7,
   *     "next_invoice_days_before_due": 5 }
   *
   * Usado por: botão "Salvar" do formulário.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function handleSubmit(): Promise<void> {
    setError(null);

    const dueSoon = csvToSortedArray(form.due_soon_days_before);
    const overdue = csvToSortedArray(form.overdue_reminder_days);
    if (!dueSoon || dueSoon.length === 0) {
      setError(t('billing.settings.errors.dueSoonInvalid'));
      return;
    }
    if (!overdue || overdue.length === 0) {
      setError(t('billing.settings.errors.overdueInvalid'));
      return;
    }
    const suspendDays = Number(form.suspend_after_days_overdue);
    const nextInvoiceDays = Number(form.next_invoice_days_before_due);
    if (!Number.isInteger(suspendDays) || suspendDays <= 0) {
      setError(t('billing.settings.errors.suspendDaysInvalid'));
      return;
    }
    if (!Number.isInteger(nextInvoiceDays) || nextInvoiceDays < 0) {
      setError(t('billing.settings.errors.nextInvoiceDaysInvalid'));
      return;
    }

    const input: UpdateBillingSettingsInput = {
      billing_email_enabled: form.billing_email_enabled,
      invoice_created_email_enabled: form.invoice_created_email_enabled,
      invoice_due_soon_email_enabled: form.invoice_due_soon_email_enabled,
      invoice_overdue_email_enabled: form.invoice_overdue_email_enabled,
      payment_success_email_enabled: form.payment_success_email_enabled,
      payment_failed_email_enabled: form.payment_failed_email_enabled,
      payment_action_required_email_enabled: form.payment_action_required_email_enabled,
      due_soon_days_before: dueSoon,
      overdue_reminder_days: overdue,
      suspend_after_days_overdue: suspendDays,
      next_invoice_days_before_due: nextInvoiceDays,
    };

    setSubmitting(true);
    try {
      await updateBillingSettingsCallable(input);
      trackEvents.billingSettingsUpdated(Object.keys(input));
      toast.success(t('billing.settings.success'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Form
      actions={
        <Button
          variant="primary"
          loading={submitting}
          onClick={(): void => {
            void handleSubmit();
          }}
        >
          {t('billing.settings.save')}
        </Button>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">{t('billing.settings.toggles')}</Header>}>
          <SpaceBetween size="s">
            {TOGGLE_KEYS.map((key) => (
              <Toggle
                key={key}
                checked={form[key]}
                onChange={({ detail }): void => {
                  setBool(key, detail.checked);
                }}
              >
                {t(`billing.settings.${key}`)}
              </Toggle>
            ))}
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2">{t('billing.settings.windows')}</Header>}>
          <SpaceBetween size="m">
            <FormField
              label={t('billing.settings.due_soon_days_before')}
              description={t('billing.settings.csvHelp')}
            >
              <Input
                value={form.due_soon_days_before}
                onChange={({ detail }): void => {
                  setForm((prev) => ({ ...prev, due_soon_days_before: detail.value }));
                }}
                placeholder="7, 3, 1"
              />
            </FormField>
            <FormField
              label={t('billing.settings.overdue_reminder_days')}
              description={t('billing.settings.csvHelp')}
            >
              <Input
                value={form.overdue_reminder_days}
                onChange={({ detail }): void => {
                  setForm((prev) => ({ ...prev, overdue_reminder_days: detail.value }));
                }}
                placeholder="7, 3, 1"
              />
            </FormField>
            <FormField label={t('billing.settings.suspend_after_days_overdue')}>
              <Input
                type="number"
                value={form.suspend_after_days_overdue}
                onChange={({ detail }): void => {
                  setForm((prev) => ({ ...prev, suspend_after_days_overdue: detail.value }));
                }}
              />
            </FormField>
            <FormField label={t('billing.settings.next_invoice_days_before_due')}>
              <Input
                type="number"
                value={form.next_invoice_days_before_due}
                onChange={({ detail }): void => {
                  setForm((prev) => ({ ...prev, next_invoice_days_before_due: detail.value }));
                }}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Form>
  );
}
